// Turning stored rich text into something a timeline row can show. Notes and tasks written in the
// SmartBox are saved as HTML ("<p>q</p>") and the API hands that back HTML-ESCAPED ("&lt;p&gt;q&lt;/p&gt;"),
// so a row that prints the raw value shows the markup instead of the text. Both layers have to come
// off — unescape the tags, strip them, then decode whatever entities are left ("&amp;" -> "&").
//
// Deliberately conservative: only sequences that really look like a tag (a letter after the "<")
// are removed, so ordinary prose like "5 < 6" or "Tom & Jerry" survives untouched.

// The handful of named entities that actually turn up in stored content. Anything else is left
// alone rather than guessed at — "&foo;" is more likely to be text than a broken entity.
const NAMED = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' }

// A tag: "<p>", "</p>", "<br/>", "<a href=…>" — always a letter (after an optional slash) so a
// lone "<" in prose is never mistaken for markup.
const TAG = /<\/?[a-zA-Z][^>]*>/g
// The same thing, escaped: "&lt;p&gt;". Same letter rule, so "5 &lt; 6" stays a comparison.
const ESCAPED_TAG = /&lt;(\/?[a-zA-Z][^&<>]*?)&gt;/g

// "&amp;" / "&#39;" / "&#x2019;" -> the character. Unknown names come back unchanged.
function decodeEntities(text) {
  // `#X2019` as well as `#x2019`: HTML allows either case, the decoder below already handles both,
  // and without it here the upper-case form was left on screen as raw "&#X2019;".
  return text.replace(/&(#\d+|#[xX][0-9a-fA-F]+|[a-zA-Z]+);/g, (match, body) => {
    if (body[0] === '#') {
      const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10)
      return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : match
    }
    const named = NAMED[body.toLowerCase()]
    return named === undefined ? match : named
  })
}

// Display-ready text: no tags, no entities, no runs of blank space — line breaks kept, since a
// note's paragraphs are part of what it says.
export function toPlainText(raw) {
  let text = String(raw ?? '')
  if (!text) return ''
  // Unescape tag-shaped sequences first (a couple of passes covers double-escaped legacy content),
  // so the stripping below sees them as the markup they are.
  for (let i = 0; i < 3 && ESCAPED_TAG.test(text); i += 1) {
    ESCAPED_TAG.lastIndex = 0
    text = text.replace(ESCAPED_TAG, '<$1>')
  }
  ESCAPED_TAG.lastIndex = 0
  return decodeEntities(text.replace(TAG, ' '))
    .replace(/[ \t]+/g, ' ')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .join('\n')
}
